<script setup>
import { Form, FormField } from "@primevue/forms";
import { zodResolver } from "@primevue/forms/resolvers/zod";
import { z } from "zod";

const { isMini } = defineProps({
  isMini: {
    type: Boolean,
    default() {
      return false;
    },
  },
});

const emits = defineEmits(["signup", "onClose"]);

const route = useRoute();
const config = useRuntimeConfig();

const { getSession } = useAuth();
const { setToken } = useAuthState();
const toast = useToast();
const authToken = ref(null);
const socialLoginLoading = ref(false);

const error = ref();
const { authSession } = useAuthSession();

const loading = ref(false);
const initialValues = ref({
  username: "",
  email: "",
  referral_code: route?.query?.ref || null,
  password: "",
  password_confirmation: "",
  term: false,
});

const resolver = zodResolver(
  z
    .object({
      name: z.string().min(1, { message: "Username is required." }),
      email: z.string().min(1, { message: "Email is required." }),
      referral_code: z.string().nullable().optional(),
      password: z
        .string()
        .min(8, { message: "Minimum 8 characters." })
        .max(20, { message: "Maximum 20 characters." })
        .refine((value) => /[a-z]/.test(value), {
          message: "Must have a lowercase letter.",
        })
        .refine((value) => /[A-Z]/.test(value), {
          message: "Must have an uppercase letter.",
        })
        .refine((value) => /[0-9]/.test(value), {
          message: "Must have a number.",
        }),
      password_confirmation: z
        .string()
        .min(1, { message: "Retype your password." }),
      term: z.boolean(),
    })
    .refine((data) => data.password === data.password_confirmation, {
      message: "Passwords don't match",
      path: ["password_confirmation"],
    })
    .refine((data) => data.term, {
      message: "You have to accept all this permission",
      path: ["term"],
    }),
);

const onFormSubmit = async ({ valid, states, values }) => {
  console.log("values", values);
  if (valid) {
    loading.value = true;
    await useApiFetch("auth/signup", {
      method: "POST",
      body: {
        ...values,
        user_type: "student",
      },
      async onResponse({ response }) {
        const session = {
          ...values,
          token: response._data?.data?.token,
        };
        authSession.value = session;

        error.value = null;
        setToken(response._data?.data?.token);
        authToken.value = response._data?.data?.token;

        if (response._data?.data?.token) {
          getSession();
          setTimeout(() => {
            if (isMini) {
              emits("onClose");
            } else {
              navigateTo("/signup/verify");
            }
          }, 500);
        }
      },
      onResponseError({ response }) {
        const errorMsg =
          response._data?.errors?.at(0) || response._data?.message;
        error.value = errorMsg;
        loading.value = false;
        if (response.status !== 422) {
          toast.add({
            severity: "error",
            summary: "Oops!",
            detail: errorMsg ?? "Internal server error.",
            life: 6000,
            closable: false,
          });
        }
      },
    });

    loading.value = false;
  }
};

const loginWithGoogle = () => {
  socialLoginLoading.value = true;
  navigateTo(config.public.apiBaseUrl + "auth/google/redirect", {
    external: true,
  });
};

const handleLoginAccount = () => {
  if (isMini) {
    emits("signin");
  } else {
    navigateTo("/signin");
  }
};
</script>

<template>
  <div class="space-y-0">
    <p v-if="isMini" class="font-bold text-slate-800">Create a new account</p>
    <h1
      v-else
      class="text-[28px] lg:text-[36px] xl:text-[40px] font-bold text-primary-500 leading-[1] mb-3"
    >
      Student Account
    </h1>

    <p class="text-sm font-normal text-[#4B5563]">
      If you already have an account,
      <span
        class="font-bold text-blue-500 cursor-pointer hover:underline"
        @click="handleLoginAccount"
      >
        Login here
      </span>
    </p>
  </div>

  <Button
    label="Continue with Google"
    severity="secondary"
    icon="pi pi-google"
    outlined
    class="text-sm lg:text-lg !border !border-[#64748B] py-1.5 lg:py-2 leading-normal min-h-[44px]"
    :class="{
      '!text-sm': isMini,
    }"
    :size="isMini ? 'small' : null"
    :loading="socialLoginLoading"
    @click.prevent="loginWithGoogle"
  >
    <template #icon>
      <img src="~/assets/img/icon/google.svg" class="h-5 w-5" />
    </template>
  </Button>

  <Divider align="center" type="solid" class="w-full">
    <b class="text-sm text-[#9E9E9E] font-normal">or continue with</b>
  </Divider>

  <Message
    v-if="error"
    severity="error"
    :pt="{
      text: 'font-normal',
    }"
  >
    {{ error }}
  </Message>
  <Form
    :initialValues
    :resolver
    @submit="onFormSubmit"
    class="flex flex-col w-full gap-6 justify-between"
    v-slot="$form"
  >
    <FormField
      v-slot="$field"
      as="section"
      name="name"
      initialValue=""
      class="flex flex-col gap-0"
    >
      <Label isRequired for="name">Name</Label>
      <InputText
        type="text"
        placeholder="Enter your name"
        fluid
        :invalid="$field?.invalid"
      />
      <Message
        v-if="$field?.invalid"
        severity="error"
        size="small"
        variant="simple"
        :pt="{
          content: 'px-1 pt-1',
        }"
      >
        {{ $field.error?.message }}
      </Message>
    </FormField>
    <FormField
      v-slot="$field"
      as="section"
      name="email"
      initialValue=""
      class="flex flex-col gap-0"
    >
      <Label isRequired for="email">Email Address</Label>
      <InputText
        type="email"
        placeholder="example@gmail.com"
        fluid
        :invalid="$field?.invalid"
      />
      <Message
        v-if="$field?.invalid"
        severity="error"
        size="small"
        variant="simple"
        :pt="{
          content: 'px-1 pt-1',
        }"
      >
        {{ $field.error?.message }}
      </Message>
    </FormField>
    <FormField
      v-slot="$field"
      as="section"
      name="referral_code"
      :initialValue="route.query.ref"
      class="flex flex-col gap-0"
    >
      <Label for="referral_code">Referral Code</Label>
      <InputText
        type="text"
        placeholder="Enter referral code"
        fluid
        :invalid="$field?.invalid"
      />
      <Message
        v-if="$field?.invalid"
        severity="error"
        size="small"
        variant="simple"
        :pt="{
          content: 'px-1 pt-1',
        }"
      >
        {{ $field.error?.message }}
      </Message>
    </FormField>
    <FormField
      v-slot="$field"
      as="section"
      name="password"
      class="flex flex-col gap-0"
    >
      <Label isRequired for="password">Password</Label>
      <Password
        type="text"
        placeholder="Password"
        :feedback="false"
        initialValue=""
        toggleMask
        fluid
        :invalid="$field?.invalid"
      />
      <Message
        v-if="$field?.invalid"
        severity="error"
        size="small"
        variant="simple"
        :pt="{
          content: 'px-1 pt-1',
        }"
      >
        {{ $field.error?.message }}
      </Message>
    </FormField>
    <FormField
      v-slot="$field"
      as="section"
      name="password_confirmation"
      initialValue=""
      class="flex flex-col gap-0"
    >
      <Label isRequired for="password_confirmation">Confirm Password</Label>
      <Password
        type="text"
        placeholder="Confirm Password"
        :feedback="false"
        toggleMask
        fluid
        :invalid="$field?.invalid"
      />
      <Message
        v-if="$field?.invalid"
        severity="error"
        size="small"
        variant="simple"
        :pt="{
          content: 'px-1 pt-1',
        }"
      >
        {{ $field.error?.message }}
      </Message>
    </FormField>

    <FormField
      v-slot="$field"
      name="term"
      :initialValue="false"
      class="flex flex-col gap-0"
    >
      <div class="flex items-center gap-2">
        <Checkbox inputId="term" binary :invalid="$field?.invalid" />
        <label for="term" class="text-sm flex-1">
          Creating an account means you are okay with our
          <NuxtLink
            href="terms-conditions"
            class="block text-blue-500 font-semibold cursor-pointer"
          >
            Terms and Conditions.
          </NuxtLink>
        </label>
      </div>
      <Message
        v-if="$field?.invalid"
        severity="error"
        size="small"
        variant="simple"
      >
        {{ $field.error?.message }}
      </Message>
    </FormField>
    <div class="flex justify-end gap-2">
      <Button
        v-if="isMini"
        class="w-full text-xs lg:text-sm self-end mt-3 min-h-[40px]"
        :class="{
          'lg:w-[180px]': !isMini,
        }"
        type="button"
        label="Maybe later"
        fluid
        outlined
        @click="emits('onClose')"
      ></Button>
      <Button
        :loading="loading"
        type="submit"
        :fluid="isMini"
        label="Create account"
        class="w-full text-xs lg:text-sm self-end mt-3 min-h-[40px]"
        :class="{
          'lg:w-[180px]': !isMini,
        }"
      />
    </div>
  </Form>
</template>
